import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MainFrontendConfigFieldAccess } from 'src/modules/app-template/netgen/MainFrontendConfigFieldAccess';
import { Company } from 'src/modules/database/shared/company.model';
import { DatabaseUserAttribute } from 'src/modules/database/shared/database-user-attribute.model';
import { DatabaseUserGroup } from 'src/modules/database/shared/database-user-group.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldListItem } from '../../netgen/FrontendFieldListItem';
import { AdminBaseEditComponent } from '../admin-base-edit/admin-base-edit.component';
import { EditGeneralUsersComponent } from '../edit-general-users/edit-general-users.component';

@Component({
    selector: 'app-edit-general-user',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditGeneralUserComponent extends AdminBaseEditComponent<DatabaseUser> {

    definition = Objects.with(EditGeneralUsersComponent.staticDefinition, "canInsert", this.app.mainConfig.adminUsersAllowAdd);

    userGroups: DatabaseUserGroup[];
    companies: Company[] = [];

    async initAdditionalData(): Promise<void> {
        this.userGroups = await this.app.loadDataHandler(async () => RestEndpoint.main().run("api/app/usergroup").list(DatabaseUserGroup), this);
        this.companies = this.app.mainConfig.useAdminCompanies ? await this.app.loadDataHandler(async () => RestEndpoint.main().run("api/app/company").list(Company), this) : [];
        (this.item as any).groups = this.item.groups.map(g => g.id);      
    }

    getForm(): FrontendForm {
        return new FrontendForm([
            this.applyFieldAccess(new FrontendField("userName", this.app.t("entity.DatabaseUser.fields.userName"), FrontendFieldType.string, {
                mandatory: true,
                isEmailAddress: this.app.mainConfig.authRules.userNameIsEmail
            })),
            this.applyFieldAccess(new FrontendField("salutation", this.app.t("entity.DatabaseUser.fields.salutation"), FrontendFieldType.dropdown, {
                dropdownEditable: true,
                listItems: FrontendFieldListItem.make(["Herr", "Frau"], null, false)
            })),
            this.applyFieldAccess(new FrontendField("firstName", this.app.t("entity.DatabaseUser.fields.firstName"), FrontendFieldType.string)),
            this.applyFieldAccess(new FrontendField("lastName", this.app.t("entity.DatabaseUser.fields.lastName"), FrontendFieldType.string)),
            this.app.mainConfig.authRules.userNameIsEmail ? null : this.applyFieldAccess(new FrontendField("mail", this.app.t("entity.DatabaseUser.fields.mail"), FrontendFieldType.string, { isEmailAddress: true})),
            this.applyFieldAccess(new FrontendField("phone", this.app.t("entity.DatabaseUser.fields.phone"), FrontendFieldType.string)),
            this.applyFieldAccess(new FrontendField("isAdmin", this.app.t("entity.DatabaseUser.fields.isAdminEdit"), FrontendFieldType.bool)),
            this.applyFieldAccess(new FrontendField("active", this.app.t("entity.DatabaseUser.fields.activeEdit"), FrontendFieldType.bool), MainFrontendConfigFieldAccess.hidden),
            this.applyFieldAccess(new FrontendField("color", this.app.t("entity.DatabaseUser.fields.color"), FrontendFieldType.color, {
                predefinedColors: Utils.getMaterialColors()
            }), MainFrontendConfigFieldAccess.hidden),
            this.app.mainConfig.useAdminCompanies ? new FrontendField("companyId", this.app.t("entity.DatabaseUser.fields.company"), FrontendFieldType.dropdown, {
                listItems: FrontendFieldListItem.make(this.companies, c => [c.id, c.name], true, [[null, "<Keine>"]]),
                dropdownCanFilter: true
            }) : null,
            this.app.mainConfig.useAdminUserGroups ? this.app.mainConfig.onlyOneGroupPerUser ?  new FrontendField("groups", this.app.t("entity.DatabaseUser.fields.groups"), FrontendFieldType.dropdown, { 
                mandatory: true,
                listItems: FrontendFieldListItem.make(this.userGroups, ug => [ug.id, ug.name], true),
                dropdownCanFilter: true,
                toForm: v => Utils.isNoe(v) ? null : v[0],
                fromForm: v => v == null ? [] : [v]
                          }) : new FrontendField("groups", this.app.t("entity.DatabaseUser.fields.groups"), FrontendFieldType.dropdownMulti, { 
                dropdownEditable: true, 
                listItems: FrontendFieldListItem.make(this.userGroups, ug => [ug.id, ug.name], true)
            }) : null,
            this.applyFieldAccess(new FrontendField("password", this.id > 0 ? this.app.t("entity.DatabaseUser.other.passwordChange") : this.app.t("entity.DatabaseUser.fields.password"), FrontendFieldType.password, { 
                mandatory: this.isInsert(),
                placeholder: this.isInsert() ? null : "●●●●●●●●" 
            })),
            ...this.app.mainConfig.additionalUserAttributes.map(a => {
                let result = Objects.cloneDeep(a);
                result.id = "attrib_" + result.id;
                return result;
            }),
            this.app.mainConfig.customer == "lemgo" ? new FrontendField("generateLetter", this.app.t("entity.DatabaseUser.other.generateLetter"), FrontendFieldType.button, {
                onClick: this.customerLemgoGenerateLetter.bind(this)
            }) : null
        ]);
    }

    async fillForm(): Promise<void> {
        await super.fillForm();
        for (let attrib of this.app.mainConfig.additionalUserAttributes) {
            let a = this.item.attributes.find(a2 => a2.key == attrib.id);
            if (a != null) {
                let field = this.form.getField("attrib_" + attrib.id);
                let v = field.type == FrontendFieldType.bool ? Utils.toBool(a.value) : a.value;
                field.setValue(v);
            }
        }
    }

    async fromForm(): Promise<void> {
        await super.fromForm();

        this.item.groups = ((this.item as any).groups as number[]).map(id => this.userGroups.find(ug => ug.id == id));
        this.item.company = null;

        if (!Utils.isNoe((this.item as any).password)) {
            if (this.id > 0 && !await this.app.dialog.yesNo(this.app.t("entity.DatabaseUser.other.passwordChangeConfirmation"), this.app.t("general.warning"))) {
                return;
            }
            (this.item as any).attrib____passwordChange = (this.item as any).password;
        }

        for (let attrib of [...this.app.mainConfig.additionalUserAttributes, new FrontendField("___passwordChange", "", FrontendFieldType.string)]) {
            if (Objects.hasProperty(this.item, "attrib_" + attrib.id)) {
                let a = this.item.attributes.find(a2 => a2.key == attrib.id);
                if (a == null) {
                    a = Objects.fromPlain(DatabaseUserAttribute, {
                        key: attrib.id
                    });
                    this.item.attributes.push(a);
                }
                a.value = this.item["attrib_" + attrib.id];
                delete this.item["attrib_" + attrib.id];
            }
        }
    }


    private applyFieldAccess(field: FrontendField, defaultAccess = MainFrontendConfigFieldAccess.editable): FrontendField {
        let fa = this.app.mainConfig.adminUsersFieldAccess;
        if (fa == null) {
            return field;
        }
        if (Objects.getSafe(fa, field.id, defaultAccess) == MainFrontendConfigFieldAccess.hidden) {
            return null;
        }
        if (Objects.getSafe(fa, field.id, defaultAccess) == MainFrontendConfigFieldAccess.readOnly) {
            field.enabled = false;
        }
        return field;
    }

    private async customerLemgoGenerateLetter(): Promise<void> {
        if (this.item.id == null || this.item.id == 0) {
            await this.app.dialog.info(this.app.t("entity.DatabaseUser.other.generateLetterAfterSave"));
            return;
        }

        await RestEndpoint.main().query({ type: "generateLetter" }).post().body({ userName: this.item.userName }).runSaveAs("api/app/customerCall", "PortalLogin.pdf");
    }

}
